import { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Box, Button, Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";

import { digitsFor, makeChoices, makeProblem, money, shuffle } from "./money.js";

const HEIGHT = 280;
const RUNNER_X = 90;
const LIVES = 3;
const STAND_H = 62;
const CROUCH_H = 34;
const JUMP_V = 11;
const GRAVITY = 0.6;
// The runner is always drawn white, with a thin dark outline so it still
// stands out on a light background.
const RUNNER_FILL = "#ffffff";
const RUNNER_EDGE = "rgba(0, 0, 0, 0.85)";

const fresh = () => ({
  x: 0, speed: 4, y: 0, vy: 0, crouch: false, obstacles: [], nextSpawn: 260, grace: 0,
});

/**
 * The side scroller. Jump over crates, duck under bars. Hit one and you must
 * solve a sum whose answer is one payment's amount: right smashes through,
 * wrong costs a life.
 */
export default function Game({ payments, fields }) {
  const theme = useTheme();
  const wrapRef = useRef(null);
  const canvasRef = useRef(null);
  const sim = useRef(fresh());
  const queue = useRef([]);
  const phaseRef = useRef("ready");
  const scoreRef = useRef(0);

  const [phase, setPhaseState] = useState("ready");
  const [question, setQuestion] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [score, setScoreState] = useState(0);
  const [lives, setLives] = useState(LIVES);
  const [best, setBest] = useState(0);

  const setPhase = (p) => {
    phaseRef.current = p;
    setPhaseState(p);
  };
  const addScore = (n) => {
    scoreRef.current += n;
    setScoreState(scoreRef.current);
    setBest((b) => Math.max(b, scoreRef.current));
  };

  const amountOf = useCallback((p) => Number(p.fields?.[fields.amount] ?? 0), [fields]);
  const currencyOf = useCallback((p) => p.fields?.[fields.currency] || "", [fields]);

  const nextPayment = () => {
    if (!queue.current.length) queue.current = shuffle(payments);
    return queue.current.pop();
  };

  const askQuestion = (kind) => {
    const p = nextPayment();
    setQuestion({
      kind,
      payment: p,
      text: makeProblem(amountOf(p), digitsFor(currencyOf(p))),
      choices: makeChoices(p, payments, amountOf, currencyOf),
    });
    setPhase("question");
  };

  const start = () => {
    sim.current = fresh();
    queue.current = shuffle(payments);
    scoreRef.current = 0;
    setScoreState(0);
    setLives(LIVES);
    setFeedback(null);
    setQuestion(null);
    setPhase("running");
  };

  const jump = () => {
    const s = sim.current;
    if (phaseRef.current === "running" && s.y === 0 && s.vy === 0) s.vy = JUMP_V;
  };
  const crouch = (on) => {
    sim.current.crouch = on && phaseRef.current === "running";
  };

  const answer = (choice) => {
    if (phaseRef.current !== "question" || !question) return;
    const s = sim.current;
    s.grace = 45;
    s.crouch = false;
    if (choice.correct) {
      addScore(2);
      setFeedback({ correct: true, payment: question.payment, chosen: choice, kind: question.kind });
      setPhase("running");
    } else {
      const nl = lives - 1;
      setLives(nl);
      setFeedback({ correct: false, payment: question.payment, chosen: choice, kind: question.kind });
      setPhase(nl <= 0 ? "over" : "running");
    }
    setQuestion(null);
  };

  // Keyboard: arrows / W S / Space to move, Enter or Space to start, 1-3 to answer.
  useEffect(() => {
    const onDown = (e) => {
      const p = phaseRef.current;
      const onPage = e.target === document.body || e.target === canvasRef.current;
      if ((p === "ready" || p === "over") && (e.key === " " || e.key === "Enter") && onPage) {
        e.preventDefault();
        start();
        return;
      }
      if (p === "running") {
        if (e.key === "ArrowUp" || e.key === "w" || e.key === "W" || (e.key === " " && onPage)) {
          e.preventDefault();
          jump();
        } else if (e.key === "ArrowDown" || e.key === "s" || e.key === "S") {
          e.preventDefault();
          crouch(true);
        }
      }
      if (p === "question" && question) {
        const i = Number(e.key) - 1;
        if (i >= 0 && i < question.choices.length) answer(question.choices[i]);
      }
    };
    const onUp = (e) => {
      if (e.key === "ArrowDown" || e.key === "s" || e.key === "S") crouch(false);
    };
    window.addEventListener("keydown", onDown);
    window.addEventListener("keyup", onUp);
    return () => {
      window.removeEventListener("keydown", onDown);
      window.removeEventListener("keyup", onUp);
    };
  });

  // The animation loop. It always draws; it only moves while running.
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    let raf;
    const pal = theme.palette;
    const ink = pal.text.primary;
    const faint = pal.text.disabled || pal.divider;
    const line = pal.divider;
    const paper = pal.background.paper || "#fff";

    const frame = () => {
      const w = wrapRef.current?.clientWidth || 600;
      const dpr = window.devicePixelRatio || 1;
      if (canvas.width !== Math.round(w * dpr)) {
        canvas.width = Math.round(w * dpr);
        canvas.height = HEIGHT * dpr;
        canvas.style.width = `${w}px`;
        canvas.style.height = `${HEIGHT}px`;
      }
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const s = sim.current;
      const ground = HEIGHT - 50;

      if (phaseRef.current === "running") {
        s.speed = Math.min(9, 4 + scoreRef.current * 0.15);
        s.x += s.speed;
        if (s.grace > 0) s.grace -= 1;

        // Spawn.
        s.nextSpawn -= s.speed;
        if (s.nextSpawn <= 0) {
          const kind = Math.random() < 0.55 ? "crate" : "bar";
          s.obstacles.push({ x: w + 30, kind, w: kind === "crate" ? 28 : 44, done: false });
          s.nextSpawn = 240 + Math.random() * 320;
        }

        // Jump physics; holding duck in the air drops you faster.
        if (s.y > 0 || s.vy !== 0) {
          s.vy -= GRAVITY + (s.crouch ? 0.9 : 0);
          s.y += s.vy;
          if (s.y <= 0) {
            s.y = 0;
            s.vy = 0;
          }
        }

        const h = s.crouch && s.y === 0 ? CROUCH_H : STAND_H;
        const rBottom = ground - s.y;
        const rTop = rBottom - h;
        const rLeft = RUNNER_X - 9;
        const rRight = RUNNER_X + 9;

        for (const o of s.obstacles) {
          o.x -= s.speed;
          if (o.done) continue;
          const top = o.kind === "crate" ? ground - 34 : ground - 110;
          const bottom = o.kind === "crate" ? ground : ground - 44;
          const overlap = rRight > o.x && rLeft < o.x + o.w && rBottom > top && rTop < bottom;
          if (overlap && s.grace === 0) {
            o.done = true;
            o.smashed = true;
            askQuestion(o.kind);
            break;
          }
          if (o.x + o.w < rLeft) {
            o.done = true;
            addScore(1);
          }
        }
        s.obstacles = s.obstacles.filter((o) => o.x > -80 && !o.smashed);
      }

      ctx.clearRect(0, 0, w, HEIGHT);

      // Far hills (parallax).
      ctx.fillStyle = faint;
      ctx.globalAlpha = 0.25;
      ctx.beginPath();
      ctx.moveTo(0, ground);
      for (let px = 0; px <= w; px += 8) {
        ctx.lineTo(px, ground - 40 - 25 * Math.sin((px + s.x * 0.2) / 70));
      }
      ctx.lineTo(w, ground);
      ctx.fill();
      ctx.globalAlpha = 1;

      // Ground with scrolling dashes.
      ctx.strokeStyle = ink;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(0, ground);
      ctx.lineTo(w, ground);
      ctx.stroke();
      ctx.strokeStyle = line;
      for (let px = -(s.x % 40); px < w; px += 40) {
        ctx.beginPath();
        ctx.moveTo(px, ground + 14);
        ctx.lineTo(px + 18, ground + 14);
        ctx.stroke();
      }

      // Obstacles: crates to jump, bars to duck under. Each is labelled with
      // its shape and an arrow, not told apart by colour.
      ctx.font = "bold 16px system-ui, sans-serif";
      ctx.textAlign = "center";
      for (const o of s.obstacles) {
        if (o.kind === "crate") {
          ctx.fillStyle = ink;
          ctx.fillRect(o.x, ground - 34, o.w, 34);
          ctx.fillStyle = paper;
          ctx.fillText("↑", o.x + o.w / 2, ground - 11);
        } else {
          ctx.strokeStyle = ink;
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.moveTo(o.x + 4, 0);
          ctx.lineTo(o.x + 4, ground - 110);
          ctx.moveTo(o.x + o.w - 4, 0);
          ctx.lineTo(o.x + o.w - 4, ground - 110);
          ctx.stroke();
          ctx.fillStyle = ink;
          ctx.fillRect(o.x, ground - 110, o.w, 66);
          ctx.fillStyle = paper;
          ctx.fillText("↓", o.x + o.w / 2, ground - 70);
        }
      }

      // The runner: white, outlined in dark so it reads in light and dark mode.
      const ducking = s.crouch && s.y === 0;
      const y = ground - s.y;
      const moving = phaseRef.current === "running" && s.y === 0;
      const stride = moving ? Math.sin(s.x / 6) * 10 : 4;
      const hip = ducking ? 14 : 22;
      const neck = ducking ? 26 : 46;
      const head = ducking ? 32 : 54;
      const lean = ducking ? 10 : 0;
      if (s.grace > 0 && Math.floor(s.grace / 5) % 2 === 0) ctx.globalAlpha = 0.4;
      const limbs = () => {
        ctx.beginPath();
        ctx.moveTo(RUNNER_X, y - hip);
        ctx.lineTo(RUNNER_X - stride, y);
        ctx.moveTo(RUNNER_X, y - hip);
        ctx.lineTo(RUNNER_X + stride, y);
        ctx.moveTo(RUNNER_X, y - hip);
        ctx.lineTo(RUNNER_X + lean, y - neck);
        ctx.moveTo(RUNNER_X + lean * 0.7, y - neck + 6);
        ctx.lineTo(RUNNER_X + lean + 10, y - neck + 14);
        ctx.moveTo(RUNNER_X + lean * 0.7, y - neck + 6);
        ctx.lineTo(RUNNER_X + lean - 8, y - neck + 16);
        ctx.stroke();
      };
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      // Outline pass.
      ctx.strokeStyle = RUNNER_EDGE;
      ctx.lineWidth = 6;
      limbs();
      ctx.beginPath();
      ctx.arc(RUNNER_X + lean, y - head, 9, 0, Math.PI * 2);
      ctx.fillStyle = RUNNER_EDGE;
      ctx.fill();
      // White pass.
      ctx.strokeStyle = RUNNER_FILL;
      ctx.lineWidth = 3.5;
      limbs();
      ctx.beginPath();
      ctx.arc(RUNNER_X + lean, y - head, 7.5, 0, Math.PI * 2);
      ctx.fillStyle = RUNNER_FILL;
      ctx.fill();
      ctx.globalAlpha = 1;

      if (phaseRef.current === "question") {
        ctx.fillStyle = ink;
        ctx.font = "bold 15px system-ui, sans-serif";
        ctx.textAlign = "left";
        ctx.fillText("Bonk! Solve it to break through ↓", RUNNER_X + 24, 28);
      }

      raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [theme, payments]);

  const describe = (p) => {
    if (!p) return "";
    const bits = [money(amountOf(p), currencyOf(p))];
    const when = p.fields?.[fields.date];
    if (when) {
      const dt = new Date(when);
      if (!Number.isNaN(dt.getTime())) bits.push(`paid ${dt.toLocaleDateString()}`);
    }
    const dir = p.fields?.direction;
    if (dir) bits.push(dir === "inbound" ? "money in" : "money out");
    const ref = p.fields?.reference;
    bits.push(ref ? `reference ${ref}` : `record ${p.id}`);
    if (p.held && p.held[fields.amount]) bits.push("amount set by a person in this org, not by the source");
    return bits.join(" · ");
  };

  const hold = (on) => (e) => {
    e.preventDefault();
    crouch(on);
  };

  return (
    <Stack spacing={2} sx={{ minWidth: 0 }}>
      <Stack direction="row" spacing={3} sx={{ flexWrap: "wrap", alignItems: "baseline" }}>
        <Typography variant="h6" component="p" aria-live="polite">Score: {score}</Typography>
        <Typography variant="h6" component="p" aria-label={`${lives} lives left`}>
          Lives: {"♥ ".repeat(Math.max(0, lives)).trim() || "none"} ({lives} left)
        </Typography>
        <Typography variant="body2" color="text.secondary">Best this session: {best}</Typography>
      </Stack>

      <Box
        ref={wrapRef}
        sx={{ position: "relative", width: "100%", border: 1, borderColor: "divider", borderRadius: 1, overflow: "hidden", touchAction: "manipulation" }}
      >
        <canvas
          ref={canvasRef}
          role="img"
          tabIndex={0}
          aria-label={`Runner game. Score ${score}, ${lives} lives left. ${phase === "question" ? "You hit an obstacle: answer the sum below." : "Up arrow or Space to jump over crates, down arrow to duck under bars."}`}
          style={{ display: "block" }}
          onPointerDown={jump}
        />
        {phase === "ready" || phase === "over" ? (
          <Box
            sx={{
              position: "absolute", inset: 0, display: "flex", flexDirection: "column",
              alignItems: "center", justifyContent: "center", gap: 1.5, p: 2,
              bgcolor: "background.paper", opacity: 0.95, textAlign: "center",
            }}
          >
            <Typography variant="h5" component="p">
              {phase === "over" ? `Game over: you scored ${score}` : "Ready to run?"}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: "52ch" }}>
              Jump over crates (marked ↑) with the up arrow, W or Space. Duck under bars
              (marked ↓) by holding the down arrow or S. Hit one and you must solve a sum whose
              answer is the amount of one of the {payments.length} payments you can see. Get it
              right to smash through, or get it wrong and lose a life.
            </Typography>
            <Button variant="contained" onClick={start} autoFocus>
              {phase === "over" ? "Play again" : "Start"}
            </Button>
          </Box>
        ) : null}
      </Box>

      {phase === "running" ? (
        <Stack direction="row" spacing={1.5}>
          <Button variant="outlined" size="large" onPointerDown={(e) => { e.preventDefault(); jump(); }} onClick={(e) => { if (e.detail === 0) jump(); }} sx={{ flex: 1 }}>
            ↑ Jump
          </Button>
          <Button
            variant="outlined"
            size="large"
            onPointerDown={hold(true)}
            onPointerUp={hold(false)}
            onPointerLeave={hold(false)}
            onPointerCancel={hold(false)}
            sx={{ flex: 1 }}
          >
            ↓ Duck (hold)
          </Button>
        </Stack>
      ) : null}

      {phase === "question" && question ? (
        <Box
          role="group"
          aria-label="Obstacle question"
          sx={{ border: 2, borderColor: "text.primary", borderRadius: 1, p: 2 }}
        >
          <Typography variant="overline" sx={{ display: "block" }}>
            You hit a {question.kind === "crate" ? "crate" : "bar"}! Solve it to break through
          </Typography>
          <Typography variant="h4" component="p" sx={{ fontWeight: 300, my: 1 }}>
            {question.text} = ?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Exactly one choice is the amount of the payment this sum was made from. Press 1, 2 or 3, or click.
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            {question.choices.map((c, i) => (
              <Button
                key={`${c.minor}-${i}`}
                variant="outlined"
                size="large"
                onClick={() => answer(c)}
                autoFocus={i === 0}
              >
                {i + 1}. {money(c.minor, c.currency)}
              </Button>
            ))}
          </Stack>
        </Box>
      ) : null}

      {feedback ? (
        <Alert severity={feedback.correct ? "success" : "warning"} variant="outlined" aria-live="polite">
          <strong>{feedback.correct ? "Correct, you smashed through! +2" : "Not quite. You lost a life."}</strong>{" "}
          {feedback.correct
            ? `That sum matched this payment: ${describe(feedback.payment)}.`
            : `You picked ${money(feedback.chosen.minor, feedback.chosen.currency)}${feedback.chosen.payment ? " (another payment)" : " (a made-up decoy)"}. The answer was this payment: ${describe(feedback.payment)}.`}
        </Alert>
      ) : null}
    </Stack>
  );
}
